import React from 'react'

import parse from 'html-react-parser'
import { string, shape, object, oneOf } from 'prop-types'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import Button from '@components/Button'
import { Image, type ImageProps } from '@components/Image'
import { Section } from '@components/Section'

const backgroundStyles = tv({
  base: 'w-full flex flex-col xl:flex-row',
  variants: {
    backgroundColor: {
      blue: 'bg-blue/[.06]',
      pink: 'bg-pink/[.06]',
      white: 'bg-white',
    },
  },
  defaultVariants: {
    backgroundColor: 'white',
  },
})

interface CTAProps {
  label: string
  href: string
  icon?: {
    name: string
    size?: string
    background?: 'red' | 'transparent' | 'white'
    color?: 'white' | 'black'
    className?: string
  }
  target?: string
}

interface FocusOnProps {
  title?: string
  abstract?: string
  image?: ImageProps
  cta?: CTAProps
  backgroundColor?: 'blue' | 'pink' | 'white'
  icon?: string
  superTitle?: string
}

const FocusOn: React.FC<FocusOnProps> = ({
  title = '',
  abstract = '',
  image = null,
  cta = null,
  backgroundColor = 'white',
  icon = null,
  superTitle = null,
}) => {
  const changeIconSizeOptions = {
    replace(domNode: any) {
      if (domNode.attribs?.width) {
        domNode.attribs.width = '100%'
      }
      if (domNode.attribs?.height) {
        domNode.attribs.height = '100%'
      }
    },
  }
  return (
    <Section>
      <Section.Content stickOnBorder={true}>
        <div
          className={twMerge(
            backgroundStyles({ backgroundColor }),
            'flex flex-col lg:flex-row items-stretch',
          )}
        >
          <div className="w-full aspect-[340/240] h-[240px] lg:h-auto flex-shrink-0 lg:aspect-[380/380] lg:min-w-[380px] lg:max-w-[380px] lg:w-[40%] xl:w-[30%] mb-6 md:mb-0 lg:mr-6 relative">
            <div className="aspect-square w-full h-full">
              {image && (
                <Image
                  src={image.src}
                  alt={image.alt}
                  srcset={image.srcset}
                  focalPoint={image.focalPoint}
                  className="object-cover h-full w-full"
                />
              )}
            </div>
          </div>
          <div className="flex-1 px-6 xl:px-24 lg:px-16 pt-5 pb-8 lg:py-14 self-center">
            {(superTitle || icon) && (
              <div className="flex flex-row items-stretch space-x-4 pb-4">
                {icon && (
                  <span className="w-9 h-9">
                    {parse(icon, changeIconSizeOptions)}
                  </span>
                )}
                {superTitle && (
                  <span className="text-grey text-md">{superTitle}</span>
                )}
              </div>
            )}
            <Section.Title title={title} className="pb-2" />
            <Section.Description
              description={abstract}
              className="pb-8 leading-[28px]"
            />
            {cta && (
              <Button
                href={cta.href}
                target={cta.target}
                icon={cta.icon}
                variant="secondary"
              >
                {cta.label}
              </Button>
            )}
          </div>
        </div>
      </Section.Content>
    </Section>
  )
}

export default FocusOn
